import { PLATFORMS } from "@/lib/outreach";

export const URL_FIELD_BY_KEY = Object.fromEntries(PLATFORMS.map((p) => [p.key, p.urlField]));
export const DEFAULT_CHANNELS = ["fb", "ig", "li"];

// Per-row follow-queue stats for the currently selected channels.
//
// Completion rule (2026-06 — "count down to zero"): a checked channel is
// HANDLED once it's been reviewed — the profile is verified/confirmed, the
// link was cleared/removed (no URL left), or it was followed — AND there's
// no candidate still sitting in "to review" (pending_socials) for it.
// Following is NOT required, so a view-only profile still completes once
// verified and a wrong profile completes once removed. A realtor is
// `complete` when EVERY checked channel is handled (nothing left to do).
//
// `targets` still counts confirmed profile URLs on checked channels, so the
// queue ORDER (most channels → least) is unchanged — only "finished" moves.
export function queueStats(row, channels) {
  const verified = row.verified_socials || {};
  const follows = row.follows || {};
  const pending = row.pending_socials || [];
  const pendingFields = new Set(pending.map((p) => p && p.field).filter(Boolean));

  let targets = 0;    // checked channels with a confirmed URL — drives ordering
  let followed = 0;   // checked channels the user has followed
  let handled = 0;    // checked channels fully reviewed
  let reviewable = 0; // checked channels that had anything to do (url or pending)

  for (const c of channels) {
    const f = URL_FIELD_BY_KEY[c];
    const url = (row[f] || "").trim();
    const hasPending = pendingFields.has(f);
    if (url) targets += 1;
    if (follows[c]) followed += 1;
    if (url || hasPending) reviewable += 1;

    let isHandled;
    if (hasPending) {
      isHandled = false;              // candidate still awaiting review
    } else if (!url) {
      isHandled = true;               // nothing to do (never had one / removed)
    } else {
      const v = verified[f];
      const isVerified = !!(v && v.url === url);
      isHandled = isVerified || !!follows[c];
    }
    if (isHandled) handled += 1;
  }

  return {
    targets,
    followed,
    handled,
    reviewable,
    hasWork: reviewable > 0,
    complete: handled === channels.length,
  };
}

// Recompute the dashboard's derived touch fields from a fresh realtor doc
// so an in-place row patch (verify / follow / accept) shows the same
// "Last touch" + "Touches" a full reload would.
export function withTouchStats(row) {
  const log = row.outreach_log || [];
  let latest = "";
  let touches = 0;
  for (const e of log) {
    // Opening a profile is audit-only — never a contact.
    if ((e.type || "").toLowerCase() === "viewed_profile") continue;
    touches += 1;
    const ts = e.timestamp || "";
    if (ts > latest) latest = ts;
  }
  const li = row.last_interaction_at || "";
  return {
    ...row,
    latest_touch_at: latest,
    last_contact_at: li > latest ? li : latest,
    touch_count: touches,
  };
}

export function nameOf(r) {
  return (r.first_name && r.last_name)
    ? `${r.first_name} ${r.last_name}`
    : (r.name || r.first_name || r.last_name || "—");
}

export function formatCurrency(n, currency = "CAD") {
  const v = Number(n) || 0;
  const cur = currency === "USD" ? "USD" : "CAD";
  // Hand-rolled prefix so BOTH currencies always show their disambiguating
  // symbol ("CA$" / "US$") regardless of the browser's locale. (Relying on
  // toLocaleString currency-style is locale-dependent: en-US drops "US$"→"$"
  // and en-CA drops "CA$"→"$", so neither locale prefixes both.) No FX
  // conversion — the amount is assumed already in the profile's currency.
  const prefix = cur === "USD" ? "US$" : "CA$";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  const num = abs.toLocaleString("en-US", {
    maximumFractionDigits: abs >= 1000 ? 0 : 2,
  });
  return `${sign}${prefix}${num}`;
}

export function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso);
    const diff = (Date.now() - then.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 2592000) return `${Math.round(diff / 86400)}d ago`;
    if (diff < 31536000) return `${Math.round(diff / 2592000)}mo ago`;
    return `${Math.round(diff / 31536000)}y ago`;
  } catch {
    return "—";
  }
}
